import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/Skeleton";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Share2, Eye, EyeOff, Loader2, Award,
  Layers, BookOpen, FileText, Image as ImageIcon, Video, Link2,
  CheckCircle2, ExternalLink, LayoutGrid,
} from "lucide-react";

const KIND_LIBRARY = [
  { v: "lesson",        l: "Lesson",        icon: BookOpen },
  { v: "worksheet",     l: "Worksheet",     icon: FileText },
  { v: "presentation",  l: "Presentation",  icon: ImageIcon },
  { v: "quiz",          l: "Quiz",          icon: FileText },
  { v: "demo_video",    l: "Demo video",    icon: Video },
  { v: "activity",      l: "Activity",      icon: Layers },
  { v: "certificate",   l: "Certificate",   icon: Award },
  { v: "achievement",   l: "Achievement",   icon: Award },
  { v: "project",       l: "Project",       icon: Layers },
  { v: "resource_pack", l: "Resource pack", icon: Layers },
  { v: "testimonial",   l: "Testimonial",   icon: FileText },
  { v: "workshop",      l: "Workshop",      icon: BookOpen },
  { v: "link",          l: "Link",          icon: Link2 },
  { v: "other",         l: "Other",         icon: Layers },
];

const LAYOUTS = [
  { v: "minimal",       l: "Minimal" },
  { v: "academic",      l: "Academic" },
  { v: "creative",      l: "Creative" },
  { v: "activity",      l: "Activity Trainer" },
  { v: "international", l: "International" },
];

export default function GrowthPortfolio() {
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ kind: "lesson", title: "", description: "", url: "", image_url: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/growth/portfolio");
      setBundle(data);
    } catch {
      toast.error("Could not load portfolio");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addItem = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      await api.post("/growth/portfolio", { ...form, title: form.title.trim() });
      setForm({ kind: "lesson", title: "", description: "", url: "", image_url: "" });
      toast.success("Added to portfolio");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally { setBusy(false); }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/growth/portfolio/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const publish = async (next) => {
    try {
      const payload = { is_public: next.is_public, layout: next.layout, tagline: next.tagline };
      const { data } = await api.post("/growth/portfolio/publish", payload);
      setBundle(data);
      toast.success(next.is_public ? "Portfolio is now public" : "Portfolio set to private");
    } catch { toast.error("Could not update portfolio settings"); }
  };

  const sharePath = bundle?.public_url_path;
  const shareUrl = useMemo(() => (sharePath ? `${window.location.origin}${sharePath}` : null), [sharePath]);

  return (
    <Layout title="Teaching Portfolio Builder">
      <Button variant="ghost" onClick={() => navigate("/growth-studio")} className="mb-3 -ml-2" data-testid="portfolio-back">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Growth Studio
      </Button>

      <div className="mb-6">
        <span className="tb-chip-amber inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <LayoutGrid className="h-3 w-3" /> Teaching Portfolio Builder
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
          Showcase your best work
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl">
          Lessons, worksheets, demo videos, certificates, projects — everything in one beautiful public page.
          GitHub × Behance × LinkedIn — for educators.
        </p>
      </div>

      {loading ? (
        <Skeleton className="h-44" />
      ) : (
        <>
          {/* Settings */}
          <div className="tb-card p-5 mb-5" data-testid="portfolio-settings">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-1.5">
              <Award className="h-3 w-3" /> Portfolio settings
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <p className="text-[11px] font-medium mb-1.5">Layout</p>
                <select
                  value={bundle.settings.layout}
                  onChange={(e) => publish({ ...bundle.settings, layout: e.target.value })}
                  className="w-full h-10 rounded-xl border border-border bg-card px-3 text-sm"
                  data-testid="portfolio-layout"
                >
                  {LAYOUTS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <p className="text-[11px] font-medium mb-1.5">Tagline</p>
                <Input
                  defaultValue={bundle.settings.tagline || ""}
                  onBlur={(e) => publish({ ...bundle.settings, tagline: e.target.value })}
                  placeholder="A passionate educator building global classrooms…"
                  className="h-10 rounded-xl"
                  data-testid="portfolio-tagline"
                />
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between rounded-xl bg-muted/40 p-3 border border-border">
              <div className="flex items-center gap-2">
                {bundle.settings.is_public ? <Eye className="h-4 w-4 text-emerald-600" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                <p className="text-sm font-medium">
                  {bundle.settings.is_public ? "Portfolio is PUBLIC" : "Portfolio is PRIVATE"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {bundle.settings.is_public && shareUrl && (
                  <a
                    href={shareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-xs text-primary hover:underline"
                    data-testid="portfolio-public-link"
                  >
                    {shareUrl} <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                )}
                <Button
                  variant={bundle.settings.is_public ? "outline" : "default"}
                  size="sm"
                  className="rounded-full"
                  onClick={() => publish({ ...bundle.settings, is_public: !bundle.settings.is_public })}
                  data-testid="portfolio-publish-toggle"
                >
                  <Share2 className="h-3.5 w-3.5 mr-1.5" /> {bundle.settings.is_public ? "Unpublish" : "Publish portfolio"}
                </Button>
              </div>
            </div>
          </div>

          {/* Add item form */}
          <div className="tb-card p-5 mb-5" data-testid="portfolio-add">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-1.5">
              <Plus className="h-3 w-3" /> Add a new item
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] font-medium mb-1.5">Type</p>
                <select
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                  className="w-full h-10 rounded-xl border border-border bg-card px-3 text-sm"
                  data-testid="portfolio-add-kind"
                >
                  {KIND_LIBRARY.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
                </select>
              </div>
              <div>
                <p className="text-[11px] font-medium mb-1.5">Title</p>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Photosynthesis Lab — Grade 8"
                  className="h-10 rounded-xl"
                  data-testid="portfolio-add-title"
                />
              </div>
              <div className="sm:col-span-2">
                <p className="text-[11px] font-medium mb-1.5">Description</p>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description / outcome"
                  className="rounded-xl min-h-[88px]"
                  data-testid="portfolio-add-desc"
                />
              </div>
              <div>
                <p className="text-[11px] font-medium mb-1.5">URL (lesson / video / certificate link)</p>
                <Input
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://…"
                  className="h-10 rounded-xl"
                  data-testid="portfolio-add-url"
                />
              </div>
              <div>
                <p className="text-[11px] font-medium mb-1.5">Image URL (optional)</p>
                <Input
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://…"
                  className="h-10 rounded-xl"
                  data-testid="portfolio-add-image"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={addItem} disabled={busy} className="rounded-full" data-testid="portfolio-add-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
                Add to portfolio
              </Button>
            </div>
          </div>

          {/* Items */}
          {bundle.items.length === 0 ? (
            <div className="tb-card p-8 text-center" data-testid="portfolio-empty">
              <LayoutGrid className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="mt-3 text-sm font-medium">No portfolio items yet</p>
              <p className="text-xs text-muted-foreground mt-1">Add your first lesson, certificate, or demo video to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="portfolio-items">
              {bundle.items.map((it) => {
                const meta = KIND_LIBRARY.find((k) => k.v === it.kind) || KIND_LIBRARY[KIND_LIBRARY.length - 1];
                const Icon = meta.icon;
                return (
                  <div key={it.item_id} className="tb-card p-4 relative" data-testid={`portfolio-item-${it.item_id}`}>
                    {it.image_url && (
                      <img
                        src={it.image_url}
                        alt=""
                        loading="lazy"
                        className="rounded-lg w-full h-32 object-cover mb-3 bg-muted"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    <div className="flex items-start gap-2">
                      <span className="h-8 w-8 rounded-lg tb-chip-lavender grid place-items-center shrink-0">
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">{meta.l}</p>
                        <p className="text-sm font-medium leading-snug">{it.title}</p>
                      </div>
                      <button
                        onClick={() => removeItem(it.item_id)}
                        className="h-7 w-7 grid place-items-center rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-500 transition"
                        aria-label="Remove"
                        data-testid={`portfolio-remove-${it.item_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {it.description && (
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-3">{it.description}</p>
                    )}
                    {it.url && (
                      <a href={it.url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center text-[11px] text-primary hover:underline">
                        Open link <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    )}
                    {it.is_published && (
                      <span className="absolute top-3 right-12 inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[10px] font-semibold px-2 py-0.5">
                        <CheckCircle2 className="h-2.5 w-2.5" /> Public
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
